// src/landings/pages/resources/CategoryPage.tsx
import { useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { getCategoryData, getAllCategories } from "../../../data/resources";
import ResourceNotFound from "../../components/resources/ResourceNotFound";
import RelatedCategories from "../../components/resources/RelatedCategories";
import ResourcesContact from "../../components/resources/ResourcesContact";
import CategoryHeader from "../../components/resources/CategoryHeader";
import CategorySidebar from "../../components/resources/CategorySidebar";
import ArticleContent from "../../components/resources/ArticleContent";
import ArticleTableOfContents from "../../components/resources/ArticleTableOfContents";

function CategoryPage() {
  const { category } = useParams<{ category: string }>();
  const location = useLocation();
  const articleRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeArticle, setActiveArticle] = useState<string | null>(null);
  const isManualScrollRef = useRef(false);

  const categoryData = getCategoryData(category || "");
  const allCategories = getAllCategories();

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsLoaded(true);
    // Reset active article when category changes
    setActiveArticle(null);
  }, [category]);

  // Handle hash navigation
  useEffect(() => {
    if (location.hash && isLoaded) {
      const id = location.hash.substring(1);
      const element = articleRefs.current[id];
      
      if (element) {
        isManualScrollRef.current = true;
        
        // Set active article immediately to update sidebar
        setActiveArticle(id);
        
        // Simple scroll with offset for header
        const headerOffset = 140;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
        
        // Reset manual scroll flag after animation completes
        setTimeout(() => {
          isManualScrollRef.current = false;
        }, 800); // slightly longer than typical scroll animation
      }
    }
  }, [location.hash, isLoaded]);

  // Simple but effective scroll spy
  useEffect(() => {
    if (!categoryData?.articles.length) return;
    
    const handleScroll = () => {
      // Skip if we're in the middle of a manual scroll
      if (isManualScrollRef.current) return;
      
      // Find all article elements and check their position
      const articles = categoryData.articles.map(article => ({
        id: article.id,
        element: articleRefs.current[article.id],
        position: articleRefs.current[article.id]?.getBoundingClientRect().top || 0
      }));
      
      // Find the first article that's near the top of the viewport (with offset for header)
      const headerOffset = 160; // slightly more than actual header to improve detection
      const currentArticle = articles.find(article => 
        article.position <= headerOffset && article.position > -200
      );
      
      // If we found an article and it's different from current active
      if (currentArticle && currentArticle.id !== activeArticle) {
        setActiveArticle(currentArticle.id);
      } else if (!currentArticle && articles.length > 0) {
        // If no article is visible at top, check for the one closest to viewport
        const closestToTop = articles.reduce((prev, current) => 
          Math.abs(current.position - headerOffset) < Math.abs(prev.position - headerOffset) 
            ? current 
            : prev
        );
        
        if (closestToTop && closestToTop.id !== activeArticle) {
          setActiveArticle(closestToTop.id);
        }
      }
    };
    
    // Throttle scroll events for performance
    let isScrolling = false;
    const onScroll = () => {
      if (!isScrolling) {
        window.requestAnimationFrame(() => {
          handleScroll();
          isScrolling = false;
        });
        isScrolling = true;
      }
    };
    
    window.addEventListener('scroll', onScroll, { passive: true });
    
    // Initial check
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [categoryData?.articles, activeArticle, isLoaded]);

  // Helper function to set article refs
  const setArticleRef = (id: string, ref: HTMLDivElement | null) => {
    articleRefs.current[id] = ref;
  };

  if (!categoryData) {
    return <ResourceNotFound type="category" />;
  }

  return (
    <div className="min-h-screen relative bg-[#fafaf8]">
      {/* Background */}
      <div className="fixed inset-0 w-full h-full overflow-hidden z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-[#fff5eb] to-[#f9f6f4]"></div>

        {/* Grid pattern background */}
        <svg
          className="absolute inset-0 w-full h-full opacity-[0.07]"
          width="100%"
          height="100%"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern
              id="grid"
              width="40"
              height="40"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M0 10 L40 10"
                stroke="#946e5d"
                strokeWidth="0.5"
                fill="none"
              />
              <path
                d="M0 20 L40 20"
                stroke="#946e5d"
                strokeWidth="0.5"
                fill="none"
              />
              <path
                d="M0 30 L40 30"
                stroke="#946e5d"
                strokeWidth="0.5"
                fill="none"
              />
              <path
                d="M10 0 L10 40"
                stroke="#946e5d"
                strokeWidth="0.5"
                fill="none"
              />
              <path
                d="M20 0 L20 40"
                stroke="#946e5d"
                strokeWidth="0.5"
                fill="none"
              />
              <path
                d="M30 0 L30 40"
                stroke="#946e5d"
                strokeWidth="0.5"
                fill="none"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        <Header />

        <main className="pb-20">
          {/* Category Header */}
          <CategoryHeader 
            title={categoryData.title}
            description={categoryData.description}
            featuredImage={categoryData.featuredImage}
          />

          <div className="container mx-auto max-w-7xl px-4">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Left Sidebar - All Categories */}
              <CategorySidebar 
                categories={allCategories}
                currentCategoryId={categoryData.id}
                activeArticle={activeArticle}
              />

              {/* Main Content */}
              <ArticleContent 
                articles={categoryData.articles}
                setArticleRefs={setArticleRef}
              />

              {/* Right Sidebar - Article Contents */}
              <ArticleTableOfContents 
                articles={categoryData.articles.map(a => ({ id: a.id, title: a.title }))}
                activeArticle={activeArticle}
              />
            </div>
          </div>

          {/* Related Categories */}
          {allCategories.filter((c) => c.id !== categoryData.id).length > 0 && (
            <div className="mt-12">
              <RelatedCategories
                currentCategory={categoryData.id}
                categories={allCategories}
              />
            </div>
          )}

          {/* Contact Support */}
          <ResourcesContact />
        </main>

        <Footer />
      </div>
    </div>
  );
}

export default CategoryPage;